import { useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Avatar,
  Box,
  IconButton,
  Typography,
  Button,
  Badge,
  Menu,
  Divider,
  Stack,
  Tooltip,
  useTheme,
  useMediaQuery,
  alpha,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import { ColorModeContext, tokens } from "../../theme";
import { useAuth } from "../../AuthContext";
import LogoBlog from "../../assets/logo.png";
import notificationSound from "../../assets/notification/newnotification.mp3";

import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import BroadcastOnHomeIcon from "@mui/icons-material/BroadcastOnHome";
import AddTaskIcon from "@mui/icons-material/AddTask";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import NotificationsOffOutlinedIcon from "@mui/icons-material/NotificationsOffOutlined";

const STAFF_IMAGE_BASE = "https://mindsinaction.com.na/assets/media/pictures/";

const POLL_INTERVAL = 10000;

const sameId = (a, b) => String(a) === String(b);

const TopBar = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const colorMode = useContext(ColorModeContext);
  const navigate = useNavigate();
  const { getData, addData, userID, userRole } = useAuth();

  const compact = useMediaQuery(theme.breakpoints.down("lg"));

  // Your own photo on the profile button; the person icon until it loads,
  // or if you have none.
  const [myImage, setMyImage] = useState("");
  useEffect(() => {
    if (!userID) return undefined;
    let live = true;
    Promise.resolve(getData("proccess.php", "staffs", userID))
      .then((res) => {
        if (live && res?.success) setMyImage(res.data?.[0]?.image || "");
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [userID, getData]);

  const [readBy, setReadBy] = useState([]);
  const [messages, setMessages] = useState([]);
  const [directUnread, setDirectUnread] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [userInteracted, setUserInteracted] = useState(false);

  const audioRef = useRef(null);
  const seenUnreadIds = useRef(new Set());
  const isFirstFetch = useRef(true);

  /* ---------------- Derived design tokens ---------------- */
  const ui = useMemo(() => {
    const accent = colors.teal[500];
    const accent2 = colors.newTeal[500];
    return {
      accent,
      accent2,
      surface: isDark ? colors.dark[700] : colors.white[900],
      surfaceAlt: isDark ? colors.dark[600] : colors.white[600],
      border: isDark
        ? alpha(colors.white[500], 0.09)
        : alpha(colors.dark[500], 0.1),
      textPrimary: colors.primary[100],
      textSecondary: isDark ? colors.gray[400] : colors.gray[300],
      shadow: isDark
        ? "0 14px 34px -22px rgba(0,0,0,0.8)"
        : "0 14px 34px -24px rgba(16,24,74,0.35)",
    };
  }, [colors, isDark]);

  /* ---------------- Audio unlock ---------------- */
  useEffect(() => {
    const handleUserInteraction = () => setUserInteracted(true);
    window.addEventListener("click", handleUserInteraction, { once: true });
    return () => window.removeEventListener("click", handleUserInteraction);
  }, []);

  useEffect(() => {
    audioRef.current = new Audio(notificationSound);
    audioRef.current.volume = 1;
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const playNotificationSound = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    try {
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          if (userInteracted) console.warn("Audio playback prevented:", error);
        });
      }
    } catch (error) {
      console.error("Audio playback failed:", error);
    }
  }, [userInteracted]);

  /* ---------------- Polling ---------------- */
  useEffect(() => {
    let cancelled = false;

    const fetchMessages = async () => {
      try {
        const [readByRes, broadcastRes, messagesRes] = await Promise.all([
          getData("proccess.php", "readBy_V2"),
          getData("proccess.php", "broadcast_V2"),
          getData("proccess.php", "messages_V2"),
        ]);

        if (cancelled) return;

        const nextReadBy = readByRes.data || [];
        const nextMessages = broadcastRes.data || [];

        setReadBy(nextReadBy);
        setMessages(nextMessages);

        const unread = nextMessages.filter(
          (bct) =>
            !nextReadBy.some(
              (r) => sameId(r.broadcastId, bct.id) && sameId(r.userId, userID)
            )
        );

        // On the first load, remember what's already unread without chiming —
        // only genuinely new arrivals should make a sound.
        const freshIds = unread
          .map((m) => String(m.id))
          .filter((id) => !seenUnreadIds.current.has(id));

        if (freshIds.length > 0) {
          if (!isFirstFetch.current) playNotificationSound();
          freshIds.forEach((id) => seenUnreadIds.current.add(id));
        }
        isFirstFetch.current = false;

        setDirectUnread(
          (messagesRes.data || []).filter(
            (msg) => Number(msg.status) === 0 && sameId(msg.receiver, userID)
          ).length
        );
      } catch (error) {
        console.error("Failed to fetch notifications:", error);
      }
    };

    fetchMessages();
    const intervalId = setInterval(fetchMessages, POLL_INTERVAL);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [userID, getData, playNotificationSound]);

  /* ---------------- Derived unread ---------------- */
  const isRead = useCallback(
    (messageId) =>
      readBy.some(
        (r) => sameId(r.broadcastId, messageId) && sameId(r.userId, userID)
      ),
    [readBy, userID]
  );

  const unreadBroadcasts = useMemo(
    () => messages.filter((m) => !isRead(m.id)),
    [messages, isRead]
  );

  const messageCount = unreadBroadcasts.length;

  const sortedMessages = useMemo(
    () =>
      [...messages].sort(
        (a, b) => new Date(b.date_created || 0) - new Date(a.date_created || 0)
      ),
    [messages]
  );

  /* ---------------- Actions ---------------- */
  const markAsRead = async (messageId) => {
    if (isRead(messageId)) return;
    try {
      const response = await addData("proccess.php", "readBy_V2", {
        broadcastId: messageId,
        userId: userID,
      });
      if (response.success) {
        if (Array.isArray(response.data)) setReadBy(response.data);
        else
          setReadBy((prev) => [...prev, { broadcastId: messageId, userId: userID }]);
      } else {
        console.error("Failed to mark message as read:", response.message);
      }
    } catch (error) {
      console.error("Error marking message as read:", error);
    }
  };

  const markAllAsRead = async () => {
    for (const message of unreadBroadcasts) {
      // Sequential on purpose — the endpoint writes one row per call.
      // eslint-disable-next-line no-await-in-loop
      await markAsRead(message.id);
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return "";
    if (timeString === "Just now") return timeString;
    const time = new Date(timeString);
    if (Number.isNaN(time.getTime())) return "";

    const isToday = time.toDateString() === new Date().toDateString();
    return isToday
      ? time.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      : time.toLocaleDateString([], {
          day: "2-digit",
          month: "short",
          hour: "2-digit",
          minute: "2-digit",
        });
  };

  /* ---------------- Nav items ---------------- */
  const restricted = userRole === "receptionist" || userRole === "user";

  const navItems = [
    {
      label: "Manage website",
      icon: <LanguageOutlinedIcon />,
      path: "/website/dashboard",
      hidden: restricted,
    },
    {
      label: "Password resets",
      icon: <ManageAccountsIcon />,
      path: "/admin/dashboard/password-reset",
      hidden: restricted,
    },
    {
      label: "Broadcast",
      icon: <BroadcastOnHomeIcon />,
      path: "/admin/dashboard/broadcast",
    },
    {
      label: "Task manager",
      icon: <AddTaskIcon />,
      path: "/tasks/dashboard",
    },
  ].filter((item) => !item.hidden);

  const iconBtnSx = {
    borderRadius: 2,
    color: ui.textSecondary,
    border: `1px solid ${ui.border}`,
    "&:hover": {
      color: ui.accent,
      borderColor: alpha(ui.accent, 0.5),
      bgcolor: alpha(ui.accent, 0.08),
    },
  };

  const badgeSx = (count) => ({
    "& .MuiBadge-badge": {
      right: 2,
      top: 2,
      fontWeight: 700,
      fontSize: 10,
      minWidth: 17,
      height: 17,
      animation: count > 0 ? "topbarPulse 1.8s ease-in-out infinite" : "none",
    },
    "@keyframes topbarPulse": {
      "0%, 100%": { transform: "scale(1)", opacity: 1 },
      "50%": { transform: "scale(1.14)", opacity: 0.82 },
    },
  });

  return (
    <Box
      component="header"
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 30,
        px: { xs: 1.5, md: 2.5 },
        py: 1.25,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        borderBottom: `1px solid ${ui.border}`,
        bgcolor: alpha(ui.surface, isDark ? 0.78 : 0.86),
        backdropFilter: "blur(14px)",
      }}
    >
      {/* Logo */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexShrink: 0,
          width: { xs: 92, sm: 110, md: 132 },
          px: isDark ? 1 : 0,
          py: isDark ? 0.6 : 0,
          borderRadius: 1.5,
          bgcolor: isDark ? alpha("#ffffff", 0.94) : "transparent",
        }}
      >
        <img src={LogoBlog} alt="Mindsinaction" style={{ width: "100%", display: "block" }} />
      </Box>

      {/* Nav */}
      <Stack
        direction="row"
        spacing={1}
        sx={{ display: { xs: "none", sm: "flex" }, flexWrap: "nowrap", minWidth: 0 }}
      >
        {navItems.map((item) =>
          compact ? (
            <Tooltip key={item.path} title={item.label}>
              <IconButton onClick={() => navigate(item.path)} sx={iconBtnSx}>
                {item.icon}
              </IconButton>
            </Tooltip>
          ) : (
            <Button
              key={item.path}
              startIcon={item.icon}
              onClick={() => navigate(item.path)}
              sx={{
                px: 1.75,
                py: 0.85,
                borderRadius: 2,
                whiteSpace: "nowrap",
                textTransform: "none",
                fontWeight: 600,
                fontSize: 13,
                color: ui.textSecondary,
                "&:hover": { color: ui.accent, bgcolor: alpha(ui.accent, 0.1) },
              }}
            >
              {item.label}
            </Button>
          )
        )}
      </Stack>

      {/* Right cluster */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
        <Tooltip title={isDark ? "Switch to light mode" : "Switch to dark mode"}>
          <IconButton onClick={colorMode.toggleColoeMode} sx={iconBtnSx}>
            {isDark ? (
              <LightModeOutlinedIcon fontSize="small" />
            ) : (
              <DarkModeOutlinedIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>

        <Tooltip title="Broadcasts">
          <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={iconBtnSx}>
            <Badge
              color="error"
              badgeContent={messageCount}
              max={99}
              invisible={messageCount === 0}
              sx={badgeSx(messageCount)}
            >
              <NotificationsOutlinedIcon fontSize="small" />
            </Badge>
          </IconButton>
        </Tooltip>

        <Tooltip title="My profile">
          <IconButton
            onClick={() =>
              navigate(userRole === "user" ? "/user/dashboard" : "/admin/dashboard/profile")
            }
            sx={iconBtnSx}
          >
            <Badge
              color="error"
              badgeContent={directUnread}
              max={99}
              invisible={directUnread === 0}
              sx={badgeSx(directUnread)}
            >
              {myImage ? (
                <Avatar
                  src={`${STAFF_IMAGE_BASE}${myImage}`}
                  alt="My profile"
                  sx={{ width: 26, height: 26, bgcolor: "transparent", color: "inherit" }}
                >
                  <PersonOutlineOutlinedIcon fontSize="small" />
                </Avatar>
              ) : (
                <PersonOutlineOutlinedIcon fontSize="small" />
              )}
            </Badge>
          </IconButton>
        </Tooltip>

        {userRole !== "user" && (
          <Tooltip title="Sign out">
            <IconButton
              onClick={() => navigate("/admin/dashboard/signout")}
              sx={{
                ...iconBtnSx,
                "&:hover": {
                  color: colors.red[500],
                  borderColor: alpha(colors.red[500], 0.5),
                  bgcolor: alpha(colors.red[500], 0.1),
                },
              }}
            >
              <LogoutOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      {/* ---------------------------------------------------------------- */}
      {/* Notifications menu                                                */}
      {/* ---------------------------------------------------------------- */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              width: { xs: 290, sm: 360 },
              maxHeight: 440,
              borderRadius: 2.5,
              bgcolor: ui.surface,
              border: `1px solid ${ui.border}`,
              boxShadow: ui.shadow,
              backgroundImage: "none",
              overflow: "hidden",
            },
          },
        }}
        MenuListProps={{ sx: { p: 0 } }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: `1px solid ${ui.border}`,
            bgcolor: alpha(ui.surfaceAlt, isDark ? 0.6 : 0.8),
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: ui.textPrimary }}>
              Broadcasts
            </Typography>
            {messageCount > 0 && (
              <Box
                component="span"
                sx={{
                  px: 0.85,
                  borderRadius: 999,
                  fontSize: 11,
                  fontWeight: 700,
                  color: ui.accent,
                  bgcolor: alpha(ui.accent, 0.16),
                }}
              >
                {messageCount} new
              </Box>
            )}
          </Stack>

          {messageCount > 0 && (
            <Button
              size="small"
              startIcon={<DoneAllIcon sx={{ fontSize: 16 }} />}
              onClick={markAllAsRead}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                fontSize: 12,
                color: ui.accent,
                "&:hover": { bgcolor: alpha(ui.accent, 0.1) },
              }}
            >
              Mark all read
            </Button>
          )}
        </Box>

        <Box sx={{ maxHeight: 372, overflowY: "auto" }}>
          {sortedMessages.length === 0 ? (
            <Stack alignItems="center" spacing={1.25} sx={{ py: 5 }}>
              <NotificationsOffOutlinedIcon
                sx={{ fontSize: 32, color: ui.textSecondary, opacity: 0.6 }}
              />
              <Typography variant="h6" sx={{ color: ui.textSecondary }}>
                No broadcasts yet
              </Typography>
            </Stack>
          ) : (
            sortedMessages.map((message) => {
              const unread = !isRead(message.id);
              return (
                <Box
                  key={message.id}
                  onClick={() => markAsRead(message.id)}
                  sx={{
                    px: 2,
                    py: 1.5,
                    display: "flex",
                    gap: 1.25,
                    cursor: unread ? "pointer" : "default",
                    borderBottom: `1px solid ${ui.border}`,
                    bgcolor: unread ? alpha(ui.accent, isDark ? 0.09 : 0.07) : "transparent",
                    transition: "background-color .18s ease",
                    "&:hover": { bgcolor: alpha(ui.accent, isDark ? 0.14 : 0.1) },
                    "&:last-of-type": { borderBottom: "none" },
                  }}
                >
                  <Box
                    sx={{
                      mt: 0.85,
                      width: 7,
                      height: 7,
                      flexShrink: 0,
                      borderRadius: "50%",
                      bgcolor: unread ? ui.accent : "transparent",
                    }}
                  />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        lineHeight: 1.55,
                        fontWeight: unread ? 700 : 400,
                        color: unread ? ui.textPrimary : ui.textSecondary,
                      }}
                    >
                      {message.message}
                      {sameId(message.senderID, userID) && " ↗"}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ mt: 0.4, fontSize: 11, color: ui.textSecondary }}
                    >
                      {formatTime(message.date_created)}
                    </Typography>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>

        <Divider sx={{ borderColor: ui.border }} />

        <Box sx={{ p: 1 }}>
          <Button
            fullWidth
            onClick={() => {
              setAnchorEl(null);
              navigate("/admin/dashboard/broadcast");
            }}
            sx={{
              py: 0.9,
              borderRadius: 1.5,
              textTransform: "none",
              fontWeight: 700,
              fontSize: 12.5,
              color: ui.textSecondary,
              "&:hover": { color: ui.accent, bgcolor: alpha(ui.accent, 0.1) },
            }}
          >
            Open broadcast centre
          </Button>
        </Box>
      </Menu>
    </Box>
  );
};

export default TopBar;